/**
 * Pure helpers behind `PhoneInput` (components/ui/phone-input.tsx). Plain JS so the
 * node test runner (`pnpm test:js`) can import them without a TypeScript loader.
 *
 * Why this exists: the input always prepends the selected country's dial code to what
 * is typed in the number box. When someone pastes a number copied from a phone's
 * contacts — which on iOS arrives as "‪+1 (650) 350‑8802‬", wrapped in
 * bidi control marks with a non-breaking hyphen — the emitted value became
 * "+1 ‪+1 (650) 350‑8802‬". Zoho rejects that whole lead with 400
 * (application 713, 2026-09-03), and the applicant never sees an error.
 *
 * The server normalises again (App\Support\PhoneNumber); this layer is what keeps the
 * value the person can SEE honest.
 */

/**
 * Strip everything a phone number cannot contain from raw keyboard/paste input.
 * Keeps digits, letters (Zoho allows "ext 12"), "+", parentheses, dashes, dots and single
 * spaces — the same alphabet App\Support\PhoneNumber keeps on the server.
 *
 * @param {string | null | undefined} raw
 * @returns {string}
 */
export function sanitizePhoneInput(raw) {
    let s = (raw ?? '').normalize('NFKC');
    // Invisible format/control characters (bidi embeddings, zero-width joiners) — removed
    // outright; they carry no gap a human intended.
    s = s.replace(/[\p{Cf}\p{Cc}]/gu, '');
    // Any Unicode dash is a hyphen.
    s = s.replace(/\p{Pd}/gu, '-');
    s = s.replace(/[^\dA-Za-z+()\-. ]/g, '');
    s = s.replace(/\s+/g, ' ');
    return s.trimStart();
}

/**
 * If `value` starts with "+", split off the longest dial code from `dialCodes` that
 * matches. Returns `{ dialCode, number }`; `dialCode` is null when the value carries no
 * recognisable code. Once a code has matched, `number` never contains a "+" — the
 * selector owns the prefix.
 *
 * @param {string} value already-sanitised input
 * @param {readonly string[]} dialCodes e.g. ['+1', '+44', '+1868']
 * @returns {{ dialCode: string | null, number: string }}
 */
export function splitDialCode(value, dialCodes) {
    if (!value.startsWith('+')) {
        return { dialCode: null, number: value.replace(/\+/g, '') };
    }

    const match = [...dialCodes].filter((code) => value.startsWith(code)).sort((a, b) => b.length - a.length)[0] ?? null;

    if (!match) {
        // Mid-typing ("+", "+3" on the way to "+353") or an unknown code: keep the leading
        // "+" so the person can finish typing and the code can still match on the next
        // keystroke. Dropping it here left "+1 650…" typed under a +44 selector as "1650…".
        // The server normaliser collapses the selector's prefix if this ever gets submitted.
        return { dialCode: null, number: '+' + value.slice(1).replace(/\+/g, '') };
    }

    const rest = value
        .slice(match.length)
        .replace(/\+/g, '')
        .replace(/^[\s.-]+/, '');

    return { dialCode: match, number: rest };
}
